import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  IconButton, Grid, TextField, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';
import { Producto } from '../../domain/entities/Producto';
import { useCart } from '../../application/context/CartContext';
import { useSession } from '../../../../core/context/SessionContext';
import { VentaApiService } from '../../infrastructure/api/VentaApiService';
import { useAuth } from 'react-oidc-context';
import { EnvioNacionalModal } from '../components/EnvioNacionalModal/EnvioNacionalModal';
import { CarritoVentaPanel } from '../components/CarritoVentaPanel';

export const VentaPage: React.FC = () => {
  const auth = useAuth();
  const { agregarAlCarrito } = useCart();
  const { terminal } = useSession();
  const [catalogo, setCatalogo] = useState<Producto[]>([]);
  const [isLoadingProductos, setIsLoadingProductos] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [isServicioModalOpen, setIsServicioModalOpen] = useState(false);
  const [busquedaCatalogo, setBusquedaCatalogo] = useState('');

  const moneda: 'COP' | 'MXN' = terminal?.pais === 'MX' ? 'MXN' : 'COP';

  useEffect(() => {
    const cargarDatos = async () => {
      setIsLoadingProductos(true);
      try {
        if (auth.user?.access_token) {
          const productos = await VentaApiService.obtenerProductosBD(auth.user.access_token, terminal?.pais ?? '');
          setCatalogo(productos || []);
        }
      } catch (error: unknown) {
        const detail = error instanceof Error ? error.message : 'Error desconocido';
        setErrorCarga(`No se pudieron cargar los datos: ${detail}`);
      } finally {
        setIsLoadingProductos(false);
      }
    };

    cargarDatos();
  }, [auth.user?.access_token, terminal?.pais]);

  const getPrecio = (producto: Producto) => moneda === 'COP' ? producto.precioCOP : producto.precioMXN;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(moneda === 'COP' ? 'es-CO' : 'es-MX', {
      style: 'currency',
      currency: moneda,
      minimumFractionDigits: moneda === 'COP' ? 0 : 2
    }).format(amount);
  };

  const catalogoFiltrado = useMemo(() => {
    const q = busquedaCatalogo.toLowerCase().trim();
    if (!q) return catalogo.filter(p => p.show !== false);
    return catalogo.filter(p =>
      p.show !== false && (
        p.descripcion.toLowerCase().includes(q) ||
        p.codigo.toLowerCase().includes(q)
      )
    );
  }, [catalogo, busquedaCatalogo]);

  const hayResultados = catalogoFiltrado.length > 0;

  const renderBuscador = () => (
    <TextField
      fullWidth
      placeholder="Buscar productos o servicios..."
      value={busquedaCatalogo}
      onChange={e => setBusquedaCatalogo(e.target.value)}
      size="small"
      sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: busquedaCatalogo ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="limpiar búsqueda" onClick={() => setBusquedaCatalogo('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        },
      }}
    />
  );

  const renderCatalogo = () => (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
        Catálogo de Productos
      </Typography>
      {renderBuscador()}
      <Box>
        {isLoadingProductos && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoadingProductos && errorCarga && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <Typography color="error">{errorCarga}</Typography>
          </Box>
        )}
        {!isLoadingProductos && !errorCarga && (
          <>
            {!hayResultados && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography color="text.secondary">No se encontraron resultados para la búsqueda.</Typography>
              </Box>
            )}
            {catalogoFiltrado.length > 0 && (
              <Grid container spacing={2}>
                {catalogoFiltrado.map((producto) => (
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={producto.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 3, borderColor: 'primary.main' }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                        <Typography variant="caption" color="text.secondary">{producto.codigo}</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2, mt: 0.5, mb: 1 }}>
                          {producto.descripcion}
                        </Typography>
                        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(getPrecio(producto))}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Button variant="contained" fullWidth disableElevation startIcon={<AddIcon />} onClick={() => agregarAlCarrito(producto)}>
                          Agregar
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  const renderCatalogoServicios = () => (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
        Catálogo de Servicios
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Card
            variant="outlined"
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-4px)', boxShadow: 3, borderColor: 'primary.main' }
            }}
          >
            <CardContent sx={{ flexGrow: 1, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <LocalShippingIcon color="primary" fontSize="small" />
                <Typography variant="caption" color="text.secondary">SRV-001</Typography>
              </Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2, mt: 0.5, mb: 1 }}>
                Envío Nacional
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Servicio de envío a nivel nacional
              </Typography>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disableElevation
                startIcon={<AddIcon />}
                onClick={() => setIsServicioModalOpen(true)}
              >
                Agregar
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Grid container spacing={3} sx={{ flexGrow: 1, height: '100%' }}>
        <Grid size={{ xs: 12, md: 8 }} sx={{ height: '100%', overflowY: 'auto', pr: 1 }}>
          {renderCatalogo()}
          {renderCatalogoServicios()}
        </Grid>
        <Grid size={{ xs: 12, md: 4 }} sx={{ height: '100%' }}>
          <CarritoVentaPanel />
        </Grid>
      </Grid>

      <EnvioNacionalModal
        open={isServicioModalOpen}
        onClose={() => setIsServicioModalOpen(false)}
        accessToken={auth.user?.access_token ?? ''}
      />
    </Box>
  );
};
